// src/pages/admin/AdminLogs.jsx
import * as React from 'react';
import {
  Paper, Box, Stack, Typography, Divider, TextField, InputAdornment,
  ToggleButtonGroup, ToggleButton, IconButton, Chip, List, ListItemButton,
  ListItemIcon, ListItemText, Collapse, CircularProgress, Alert, Tooltip, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import { api } from '../../lib/api';

const BLUE = '#0466c8';
const BLUE_BG = 'rgba(11,95,255,0.06)';
const BORDER = '#e6efe8';
const money = (n) => Number(n || 0).toFixed(2);
const fmtDt = (d) => (d ? new Date(d).toLocaleString() : '—');

function safeParse(json) { try { return JSON.parse(json || '[]'); } catch { return []; } }

export default function AdminLogs() {
  const [users, setUsers] = React.useState([]);
  const [carts, setCarts] = React.useState([]);
  const [txs, setTxs] = React.useState([]); // transactions
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');

  // Filtres
  const [q, setQ] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('all'); // all | users | logins | carts | tx
  const [cartConnFilter, setCartConnFilter] = React.useState('all'); // all | withUser | anonymous
  const [cartQtyFilter, setCartQtyFilter] = React.useState('all');   // all | nonempty | empty
  const [cartCtxFilter, setCartCtxFilter] = React.useState('all');   // all | tarifs | recap

  const [openMap, setOpenMap] = React.useState({}); // id -> bool

  const loadAll = async () => {
    setErr(''); setLoading(true);
    try {
      const [uRes, cRes, tRes] = await Promise.all([
        api.get('/users'),
        api.get('/cart-sessions'),
        api.get('/transactions'),
      ]);
      setUsers(Array.isArray(uRes.data) ? uRes.data : []);
      setCarts(Array.isArray(cRes.data) ? cRes.data : []);
      setTxs(Array.isArray(tRes.data) ? tRes.data : []);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Impossible de charger les logs.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { loadAll(); }, []);

  // ✅ Grouper paniers en prenant le snapshot le plus récent, et en ECRASANT userId par null si la version la plus récente est anonyme
  const groupedCarts = React.useMemo(() => {
    const byId = new Map();
    for (const c of (carts || [])) {
      const id = String(c.cartId);
      const prev = byId.get(id);

      if (!prev) {
        byId.set(id, { ...c });
        continue;
      }

      const prevFirst = new Date(prev.firstSeenAt || prev.lastSeenAt || Date.now());
      const prevLast  = new Date(prev.lastSeenAt  || prev.firstSeenAt || 0);
      const currFirst = new Date(c.firstSeenAt    || c.lastSeenAt     || Date.now());
      const currLast  = new Date(c.lastSeenAt     || c.firstSeenAt    || 0);

      const isCurrNewer = currLast >= prevLast;

      byId.set(id, {
        ...prev,
        // bornes temporelles
        firstSeenAt: (currFirst < prevFirst ? currFirst : prevFirst),
        lastSeenAt:  (currLast  > prevLast  ? currLast  : prevLast),

        // ⚠️ si le snapshot le plus récent a userId null, on écrase bien par null
        userId:      isCurrNewer ? (c.userId ?? null)      : (prev.userId ?? null),
        user:        isCurrNewer ? (c.user ?? null)        : (prev.user ?? null),

        // champs dérivés, basés sur le plus récent
        totalQty:    isCurrNewer ? Number(c.totalQty ?? 0)    : Number(prev.totalQty ?? 0),
        totalAmount: isCurrNewer ? Number(c.totalAmount ?? 0) : Number(prev.totalAmount ?? 0),
        lastContext: isCurrNewer ? (c.lastContext ?? null)    : (prev.lastContext ?? null),
        lastIp:      isCurrNewer ? (c.lastIp ?? null)         : (prev.lastIp ?? null),
        userAgent:   isCurrNewer ? (c.userAgent ?? null)      : (prev.userAgent ?? null),
      });
    }
    return Array.from(byId.values());
  }, [carts]);

  // Construire les "logs"
  const logs = React.useMemo(() => {
    const out = [];

    // comptes + connexions/déconnexions
    for (const u of users) {
      if (u?.createdAt) {
        out.push({
          _key: `user_created:${u.id}:${u.createdAt}`,
          at: new Date(u.createdAt),
          type: 'user_created',
          title: `Compte créé — ${u.email}`,
          user: u,
        });
      }
      if (u?.lastLoginAt) {
        out.push({
          _key: `user_login:${u.id}:${u.lastLoginAt}`,
          at: new Date(u.lastLoginAt),
          type: 'user_login',
          title: `Connexion — ${u.email}`,
          user: u,
        });
      }
      if (u?.lastLogoutAt) {
        out.push({
          _key: `user_logout:${u.id}:${u.lastLogoutAt}`,
          at: new Date(u.lastLogoutAt),
          type: 'user_logout',
          title: `Déconnexion — ${u.email}`,
          user: u,
        });
      }
    }

    // paniers
    for (const c of groupedCarts) {
      out.push({
        _key: `cart:${c.id}:${c.cartId}`,
        at: new Date(c.lastSeenAt || c.firstSeenAt || Date.now()),
        type: 'cart',
        title: `Panier ${c.userId ? 'connecté' : 'anonyme'} — #${c.cartId}`,
        cart: c,
      });
    }

    // transactions (création)
    for (const tx of (txs || [])) {
      out.push({
        _key: `tx:${tx.id}`,
        at: new Date(tx.createdAt),
        type: 'tx',
        title: `Transaction — ${tx.numPackage || tx.id} • ${tx?.user?.email || '—'}`,
        tx,
      });
    }

    out.sort((a, b) => b.at - a.at);
    return out.slice(0, 500);
  }, [users, groupedCarts, txs]);

  // Filtres
  const filtered = logs.filter((row) => {
    const text = (q || '').trim().toLowerCase();
    if (text) {
      const hay = [
        row.title,
        row?.user?.email,
        row?.cart?.cartId,
        row?.cart?.lastIp,
        row?.cart?.user?.email,
        row?.tx?.numPackage,
        row?.tx?.user?.email,
      ].map(x => String(x || '').toLowerCase());
      if (!hay.some(h => h.includes(text))) return false;
    }

    if (typeFilter === 'users' && !(row.type === 'user_created')) return false;
    if (typeFilter === 'logins' && !(row.type === 'user_login' || row.type === 'user_logout')) return false;
    if (typeFilter === 'carts' && row.type !== 'cart') return false;
    if (typeFilter === 'tx' && row.type !== 'tx') return false;

    if (row.type === 'cart') {
      if (cartConnFilter === 'withUser' && !row.cart?.userId) return false;
      if (cartConnFilter === 'anonymous' && !!row.cart?.userId) return false;

      const qty = Number(row.cart?.totalQty || 0);
      if (cartQtyFilter === 'empty' && qty !== 0) return false;
      if (cartQtyFilter === 'nonempty' && qty <= 0) return false;

      if (cartCtxFilter === 'tarifs' && row.cart?.lastContext !== 'tarifs') return false;
      if (cartCtxFilter === 'recap'  && row.cart?.lastContext !== 'recap')  return false;
    }
    return true;
  });

  // Actions
  const toggle = (k) => setOpenMap((m) => ({ ...m, [k]: !m[k] }));

  const deleteCart = async (id) => {
    try {
      await api.delete(`/cart-sessions/${id}`);
      setCarts(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      setErr(e?.response?.data?.message || 'Échec suppression du panier.');
    }
  };

  const purgeEmpty = async () => {
    try {
      await api.delete('/cart-sessions/purge-empty');
      await loadAll();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Échec purge paniers vides.');
    }
  };

  // UI
  const iconFor = (t) => {
    switch (t) {
      case 'user_created': return <PersonAddAlt1Icon />;
      case 'user_login':   return <LoginOutlinedIcon />;
      case 'user_logout':  return <LogoutOutlinedIcon />;
      case 'cart':         return <ShoppingCartOutlinedIcon />;
      case 'tx':           return <ReceiptLongOutlinedIcon />;
      default:             return <FilterAltOutlinedIcon />;
    }
  };

  const rightChips = (row) => {
    if (row.type === 'cart') {
      const c = row.cart;
      const qty = Number(c?.totalQty || 0);
      const amt = Number(c?.totalAmount || 0);
      return (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${qty} pcs`} />
          <Chip size="small" label={`${money(amt)} €`} />
          {c?.lastContext ? <Chip size="small" label={c.lastContext} /> : null}
        </Stack>
      );
    }
    if (row.type === 'tx') {
      const t = row.tx;
      return (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={t?.numPackage || String(t.id).slice(0,8)} />
          {t?.statusReception ? <Chip size="small" label="Reçu" /> : null}
          {t?.statusControlQuality ? <Chip size="small" label="Contrôlé" /> : null}
          {t?.statusPayment ? <Chip size="small" label="Payé" /> : null}
        </Stack>
      );
    }
    return row?.user?.role ? <Chip size="small" label={row.user.role} /> : null;
  };

  return (
    <Paper elevation={0} sx={{ p: { xs:2, sm:3 }, borderRadius: 3, border: `1px solid ${BORDER}`, background: '#fff' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs:'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 900, color: BLUE }}>Logs</Typography>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <TextField
            size="small"
            placeholder="Recherche (email / cartId / package)"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ minWidth: { xs: 0, sm: 260 }, flex: { xs: 1, sm: 'unset' } }}
          />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={typeFilter}
            onChange={(_,v)=>{ if (v) setTypeFilter(v); }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none' }, '& .Mui-selected': { bgcolor: BLUE_BG, color: BLUE } }}
          >
            <ToggleButton value="all"><FilterAltOutlinedIcon sx={{ mr: 0.5 }} fontSize="small" />Tous</ToggleButton>
            <ToggleButton value="users">Comptes</ToggleButton>
            <ToggleButton value="logins">Connexions</ToggleButton>
            <ToggleButton value="carts">Paniers</ToggleButton>
            <ToggleButton value="tx">Transactions</ToggleButton>
          </ToggleButtonGroup>

          {typeFilter === 'carts' && (
            <>
              <ToggleButtonGroup
                size="small" exclusive value={cartConnFilter}
                onChange={(_,v)=>{ if (v) setCartConnFilter(v); }}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none' }, '& .Mui-selected': { bgcolor: BLUE_BG, color: BLUE } }}
              >
                <ToggleButton value="all">Tous</ToggleButton>
                <ToggleButton value="withUser">Connectés</ToggleButton>
                <ToggleButton value="anonymous">Déconnectés</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                size="small" exclusive value={cartQtyFilter}
                onChange={(_,v)=>{ if (v) setCartQtyFilter(v); }}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none' }, '& .Mui-selected': { bgcolor: BLUE_BG, color: BLUE } }}
              >
                <ToggleButton value="all">Qté: tous</ToggleButton>
                <ToggleButton value="nonempty">&gt; 0</ToggleButton>
                <ToggleButton value="empty">= 0</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                size="small" exclusive value={cartCtxFilter}
                onChange={(_,v)=>{ if (v) setCartCtxFilter(v); }}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none' }, '& .Mui-selected': { bgcolor: BLUE_BG, color: BLUE } }}
              >
                <ToggleButton value="all">Contexte</ToggleButton>
                <ToggleButton value="tarifs">Tarifs</ToggleButton>
                <ToggleButton value="recap">Recap</ToggleButton>
              </ToggleButtonGroup>
            </>
          )}

          <IconButton onClick={loadAll} aria-label="Rafraîchir" sx={{ color: BLUE }}>
            <RefreshIcon />
          </IconButton>
          {typeFilter === 'carts' && (
            <Tooltip title="Supprimer tous les paniers vides">
              <span>
                <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />} onClick={purgeEmpty}>
                  Purger vides
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2, borderColor: BORDER }} />

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18}/><Typography>Chargement…</Typography></Stack>
      ) : filtered.length === 0 ? (
        <Typography sx={{ color: 'text.secondary' }}>Aucun log</Typography>
      ) : (
        <List dense sx={{ m: 0 }}>
          {filtered.map((row) => {
            const open = !!openMap[row._key];
            const right = (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ color:'text.secondary', whiteSpace:'nowrap' }}>
                  {fmtDt(row.at)}
                </Typography>
                {rightChips(row)}
                {row.type === 'cart' && (
                  <Tooltip title="Supprimer ce panier">
                    <IconButton size="small" onClick={(e)=>{ e.stopPropagation(); deleteCart(row.cart.id); }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Stack>
            );

            return (
              <Box key={row._key} sx={{ mb: 1.1 }}>
                <ListItemButton
                  onClick={()=>setOpenMap(m=>({ ...m, [row._key]: !m[row._key] }))}
                  sx={{
                    borderRadius: 2,
                    p: { xs: 1.1, sm: 1.25 },
                    border: `1px solid ${BORDER}`,
                    background: '#fff',
                    '&:hover': { borderColor: '#cfe1ff', background: BLUE_BG },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: BLUE }}>{iconFor(row.type)}</ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontWeight: 700 }}>{row.title}</Typography>}
                    secondary={
                      row.type === 'cart' && row.cart?.user?.email
                        ? `Utilisateur: ${row.cart.user.email}`
                        : row.type === 'tx'
                          ? `Créé le ${fmtDt(row.tx.createdAt)} • Estimé ${money(row.tx.priceEstimate)} €`
                          : (row.type === 'cart'
                              ? `Créé le ${fmtDt(row.cart.firstSeenAt)} • Maj le ${fmtDt(row.cart.lastSeenAt)}`
                              : null)
                    }
                  />
                  {right}
                </ListItemButton>

                <Collapse in={open} timeout="auto" unmountOnExit>
                  <Box sx={{ px: { xs: 1.1, sm: 1.75 }, pb: 1.25 }}>
                    <Divider sx={{ my: 1 }} />

                    {row.type === 'user_created' && row.user && (
                      <Stack spacing={0.5} sx={{ fontSize: 14 }}>
                        <Row k="Email" v={row.user.email} />
                        <Row k="Rôle" v={row.user.role} />
                        <Row k="Créé le" v={fmtDt(row.user.createdAt)} />
                      </Stack>
                    )}

                    {(row.type === 'user_login' || row.type === 'user_logout') && row.user && (
                      <Stack spacing={0.5} sx={{ fontSize: 14 }}>
                        <Row k="Email" v={row.user.email} />
                        <Row k={row.type === 'user_login' ? 'Dernière connexion' : 'Dernière déconnexion'}
                             v={fmtDt(row.type === 'user_login' ? row.user.lastLoginAt : row.user.lastLogoutAt)} />
                      </Stack>
                    )}

                    {row.type === 'cart' && row.cart && (
                      <Stack spacing={0.5} sx={{ fontSize: 14 }}>
                        <Row k="Cart ID" v={row.cart.cartId} mono />
                        <Row k="Utilisateur" v={row.cart.user?.email || '—'} />
                        <Row k="Connecté" v={row.cart.userId ? 'Oui' : 'Non'} />
                        <Row k="Quantité" v={String(row.cart.totalQty)} />
                        <Row k="Montant" v={`${money(row.cart.totalAmount)} €`} />
                        <Row k="Contexte" v={row.cart.lastContext || '—'} />
                        <Row k="Créé le" v={fmtDt(row.cart.firstSeenAt)} />
                        <Row k="Dernière modification" v={fmtDt(row.cart.lastSeenAt)} />
                        <Row k="IP" v={row.cart.lastIp || '—'} mono />
                        <Row k="User-Agent" v={row.cart.userAgent || '—'} mono long />
                        <Box>
                          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Articles</Typography>
                          <ItemsTable items={safeParse(row.cart.itemsJson)} />
                        </Box>
                      </Stack>
                    )}

                    {row.type === 'tx' && row.tx && (
                      <Stack spacing={0.5} sx={{ fontSize: 14 }}>
                        <Row k="Package" v={row.tx.numPackage || row.tx.id} mono />
                        <Row k="Client" v={row.tx?.user?.email || '—'} />
                        <Row k="Créé le" v={fmtDt(row.tx.createdAt)} />
                        <Row k="Montant estimé" v={`${money(row.tx.priceEstimate)} €`} />
                        <Row k="Articles" v={(row.tx.items || []).length} />
                      </Stack>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}
    </Paper>
  );
}

function Row({ k, v, mono=false, long=false }) {
  return (
    <Stack direction={{ xs:'column', sm:'row' }} spacing={0.5}>
      <Typography sx={{ minWidth: 220, fontWeight: 700 }}>{k}</Typography>
      <Typography sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: long ? 'break-all' : 'break-word', color: 'text.secondary' }}>
        {String(v || '—')}
      </Typography>
    </Stack>
  );
}

function ItemsTable({ items }) {
  if (!Array.isArray(items) || items.length === 0) return <Typography sx={{ color: 'text.secondary' }}>—</Typography>;
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '2fr 0.5fr 0.7fr' },
      columnGap: 1, rowGap: 0.5, p: 1,
      border: `1px dashed ${BORDER}`, borderRadius: 1, bgcolor: '#fff'
    }}>
      <Typography sx={{ fontWeight: 700 }}>Libellé</Typography>
      <Typography sx={{ fontWeight: 700, textAlign: { sm: 'right' } }}>Qté</Typography>
      <Typography sx={{ fontWeight: 700, textAlign: { sm: 'right' } }}>PU (€)</Typography>
      {items.map((it, idx) => (
        <React.Fragment key={idx}>
          <Typography sx={{ color: 'text.secondary' }}>{String(it?.label || it?.id || '—')}</Typography>
          <Typography sx={{ textAlign: { sm: 'right' } }}>{Number(it?.qty || 0)}</Typography>
          <Typography sx={{ textAlign: { sm: 'right' } }}>{money(it?.price || 0)}</Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}